import React from 'react'
import { LazyLoadComponent } from 'react-lazy-load-image-component'
import styled from 'styled-components'
import { graphql, Link, ReplaceComponentRendererArgs } from 'gatsby'

import Angle from '../atoms/Angle'
import Button from '../atoms/Button'
import Heading from '../atoms/Heading'
import Image from '../atoms/Image'
import Layout from '../organisms/Layout'
import OuterLink from '../atoms/OuterLink'
import ReviewList from '../organisms/ReviewList'
import SEO from '../molecules/SEO'
import useMenu from '../hooks/useMenu'
import { SidebarRight } from '../organisms/Sidebar'
import { MysqlProducts, MysqlSurveyAnswersEdge, Query } from '../graphqlTypes'

const Container = styled.div`
  margin: 20px 16px 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    align-items: flex-start;
    display: flex;
    margin: 40px 0 0;
  }
`

const HeroImage = styled(Image)`
  width: 100%;
`

const Article = styled.article`
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    flex: 1;
    margin: 0 110px 0;
  }
`

const PageHeading = styled.h1`
  color: ${({ theme }) => theme.palette.font.dark};
  font-size: 14px;
  font-weight: bold;
  margin: 20px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 24px;
    margin: 40px 0 0;
  }
`

const PageDescription = styled.p`
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.main};
  color: ${({ theme }) => theme.palette.font.main};
  font-size: 12px;
  margin: 12px 0 0;
  padding: 0 0 20px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 14px;
    margin: 30px 0 0;
    padding: 0 0 34px;
  }
`

const ProductHeading = styled(Heading)<{ gutterTop: boolean }>`
  && {
    background: transparent;
    border-left: 3px solid ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.font.main};
    display: flex;
    font-size: 14px;
    height: 32px;
    justify-content: start;
    margin: ${({ gutterTop }) => (gutterTop ? '38px' : '20px')} 0;
    padding: 0 0 0 8px;
    @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
      border-width: 0 0 0 5px;
      font-size: 20px;
      margin: ${({ gutterTop }) => (gutterTop ? '56px' : '30px')} 0 0;
    }
  }
`

const Product = styled.section`
  margin: 24px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 34px 0 0;
  }
`

const Manufacturer = styled.p`
  color: ${({ theme }) => theme.palette.font.sub};
  font-size: 10px;
  font-weight: bold;
  margin: 0 0 4px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 16px;
  }
`

const ProductName = styled.h3`
  color: ${({ theme }) => theme.palette.font.main};
  font-size: 14px;
  font-weight: bold;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 20px;
  }
`

const ProductImageLink = styled(Link)`
  display: block;
  text-decoration: none;
`

const ProductImage = styled.img`
  display: block;
  margin: 20px auto 0;
  object-fit: contain;
  width: 100%;
  max-height: 300px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    width: 300px;
    height: 300px;
    margin: 24px auto 0;
  }
`

const ReviewListContainer = styled.div`
  margin: 20px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 14px 0 0;
  }
`

const ProductLink = styled(Link)`
  display: block;
  margin: 16px 0 0;
  text-decoration: none;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 30px auto 0;
    width: 350px;
  }
`

const ButtonAngle = styled(Angle)`
  width: 24px;
  height: 24px;
  margin: 0 0 0 5px;
  object-fit: cover;
`

const SocialShare = styled.aside`
  margin: 44px 20px 4px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    align-items: center;
    display: flex;
    justify-content: center;
    margin: 98px 0 0;
  }
`

const SocialShareBalloon = styled(Image)`
  display: block;
  margin: 0 auto;
  width: 87px;
  height: auto;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0;
  }
`

const SocialShareText = styled.p`
  color: ${({ theme }) => theme.palette.font.main};
  font-size: 10px;
  font-weight: bold;
  margin: 8px 0 0;
  text-align: center;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 13px;
    margin: 0 0 0 12px;
    text-align: left;
    width: 350px;
  }
`

const SocialShareButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  margin: 12px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0 0 0 26px;
  }
`

const SocialShareButton = styled(OuterLink)`
  display: block;
  margin: 0 0 0 15px;
  text-decoration: none;
  width: 160px;
  &:first-child {
    margin: 0;
  }
`

type Props = ReplaceComponentRendererArgs['props'] & {
  data: Query
  pageContext: {
    awardId: number
    description: string
    image: string
    pagePath: string
    title: string
    rows: {
      title: string
      product: MysqlProducts
      surveyAnswers: MysqlSurveyAnswersEdge[]
      highlightWords: string[]
    }[]
  }
}

export default function CategoryTemplate(props: Props) {
  const { allMysqlCategories, allMysqlRanks, site } = props.data
  const { description, image, pagePath, title, rows } = props.pageContext
  const menu = useMenu(allMysqlCategories.edges, allMysqlRanks.group)

  return (
    <Layout menu={menu}>
      <SEO
        description={description}
        image={`${process.env.GATSBY_CDN_URL}/2019_autumn/feature/${image}`}
        title={`${title} | ${site!.siteMetadata!.title}`}
        type="article"
        url={`https://award.mamari.jp/${pagePath}`}
      />
      <Container>
        <Article>
          <HeroImage alt={title} filename={image} />
          <PageHeading>{title}</PageHeading>
          <PageDescription>{description}</PageDescription>
          {rows.map(({ highlightWords, product, surveyAnswers, title }, index) => (
            <section key={index}>
              <ProductHeading gutterTop={index !== 0} variant="h2">
                {title}
              </ProductHeading>
              <Product>
                <Manufacturer>{product.manufacturer}</Manufacturer>
                <ProductName>{product.name}</ProductName>
                <ProductImageLink to={product.fields!.pagePath!}>
                  <LazyLoadComponent>
                    <ProductImage alt={product.name!} src={`${product.fields!.imageUrl}?auto=format&max-h=300`} />
                  </LazyLoadComponent>
                </ProductImageLink>
              </Product>
              <ReviewListContainer>
                <ReviewList basic compact highlightWords={highlightWords} surveyAnswers={surveyAnswers} />
              </ReviewListContainer>
              <ProductLink to={product.fields!.pagePath!}>
                <Button color="white">
                  商品の詳細・口コミをみる
                  <ButtonAngle color="pink" direction="right" />
                </Button>
              </ProductLink>
            </section>
          ))}
        </Article>
        <SidebarRight />
      </Container>

      <SocialShare>
        <SocialShareBalloon alt="赤ちゃんアイコン" filename="icon-baby.png" />
        <SocialShareText>
          口コミは参考になりましたか？
          <br />
          周りのママたちにシェアしていただけると嬉しいです！
        </SocialShareText>
        <SocialShareButtonGroup>
          <SocialShareButton
            to={`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(
              `https://award.mamari.jp/2019_autumn/feature/${pagePath}`
            )}`}
          >
            <Image filename="share-line.png" />
          </SocialShareButton>
          <SocialShareButton
            to={`https://twitter.com/share?url=${encodeURIComponent(
              `https://award.mamari.jp/2019_autumn/feature/${pagePath}`
            )}&text=${encodeURIComponent(
              `【#ママリ口コミ大賞 ${title}】先輩ママたちに選び抜かれたおすすめアイテム！口コミと評判をチェック→`
            )}`}
          >
            <Image filename="share-twitter.png" />
          </SocialShareButton>
        </SocialShareButtonGroup>
      </SocialShare>
    </Layout>
  )
}

export const query = graphql`
  query($awardId: Int) {
    allMysqlCategories(filter: { parent_id: { eq: null } }) {
      edges {
        node {
          id
          mysqlId
          title
          categories {
            id
            mysqlId
            title
            fields {
              imageUrl
              pagePath
            }
          }
        }
      }
    }
    allMysqlRanks(filter: { award_id: { eq: $awardId }, rank: { eq: 1 } }) {
      group(field: category_id) {
        fieldValue
        edges {
          node {
            id
            mysqlId
            category_id
            rank
            products {
              id
              mysqlId
              category_id
              fields {
                imageUrl
              }
            }
          }
        }
      }
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`
